import { useTranslation } from 'react-i18next';
import { MenuItem } from '@ui/menu-item.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ui/card.tsx';
import {
  User,
  Settings,
  HelpCircle,
  PackageIcon,
  FactoryIcon,
  MegaphoneIcon,
  FileClock
} from 'lucide-react';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';
import { Link, useLocation } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useUser } from '@r/api/auth.tsx';
import { useContext } from 'react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { isPurchaseProofFeatureEnabled } from '@client/components/invoices/purchase-proof-feature.ts';

export function Menu() {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useUser().data;
  const workspaceConfig = useContext(WorkspaceConfigContext);
  const purchaseProofEnabled = isPurchaseProofFeatureEnabled(workspaceConfig);

  function isPathActive(paths: string[]) {
    for (const path of paths) {
      if (path.endsWith('/*')) {
        const base = path.replace('/*', '');

        if (location.pathname.startsWith(base)) {
          return true;
        }
      } else if (location.pathname === path) {
        return true;
      }
    }
    return false;
  }

  return (
    <Card className="flex h-[100%] flex-col overflow-hidden border-0">
      <CardHeader className="flex-row justify-center items-center space-y-0 text-center">
        <div className="relative mr-4 avatarContainer">
          <Avatar>
            {user?.avatar ? (
              <AvatarImage src={user?.avatar} />
            ) : (
              <AvatarFallback text={`${user?.firstname} ${user?.lastname}`} initials={true} />
            )}
          </Avatar>
        </div>

        <div className="pl-5 text-left">
          <CardTitle className="font-author-complete font-regular text-[1.2rem]">
            {`${user?.firstname} ${user?.lastname}`}
          </CardTitle>

          <CardDescription className="flex flex-col space-y-2">
            <span className="text-sm font-light text-muted-foreground">
              {t('account.unique.code', {
                uniqueCode: user?.visibleWorkspaces[0].uniqueCode
              })}
            </span>

            <span className="text-foreground">
              {t('account.membership.duration', {
                duration: format(user?.visibleWorkspaces[0].createdAt as string, 'dd/MM/yyyy')
              })}
            </span>
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col pt-6 h-fit grow">
        <div className="flex flex-col space-y-2 grow">
          <LinkProtected to="/account">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/account']) ? 'bg-muted' : ''}`}
            >
              <User />
              {t('menu.account.informations')}
            </MenuItem>
          </LinkProtected>

          <LinkProtected to="/personal-informations">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/personal-informations']) ? 'bg-muted' : ''}`}
            >
              <Settings />
              {t('menu.account.personal.informations')}
            </MenuItem>
          </LinkProtected>

          <LinkProtected to="/company-informations">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/company-informations']) ? 'bg-muted' : ''}`}
            >
              <FactoryIcon />
              {t('menu.account.company.informations')}
            </MenuItem>
          </LinkProtected>

          <LinkProtected to="/orders">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/orders', '/orders/*']) ? 'bg-muted' : ''}`}
            >
              <PackageIcon />
              {t('menu.account.order.tracking')}
            </MenuItem>
          </LinkProtected>

          {purchaseProofEnabled && (
            <Link to="/user/invoices">
              <MenuItem
                onClick={() => {}}
                className={`${isPathActive(['/user/invoices']) ? 'bg-muted' : ''}`}
              >
                <FileClock />
                {t('menu.account.purchase.proofs')}
              </MenuItem>
            </Link>
          )}

          <LinkProtected to="/changelogs">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/changelogs']) ? 'bg-muted' : ''}`}
            >
              <MegaphoneIcon />
              {t('menu.account.program.news')}
            </MenuItem>
          </LinkProtected>

          <LinkProtected to="/contact">
            <MenuItem
              onClick={() => {}}
              className={`${isPathActive(['/contact']) ? 'bg-muted' : ''}`}
            >
              <HelpCircle />
              {t('menu.account.personal.need.help')}
            </MenuItem>
          </LinkProtected>

          {/*<LinkProtected to="/">*/}
          {/*  <MenuItem onClick={() => {}} className={`${isPathActive(['/']) ? 'bg-muted' : ''}`}>*/}
          {/*    <StarIcon />*/}
          {/*    {t('menu.account.orders')}*/}
          {/*  </MenuItem>*/}
          {/*</LinkProtected>*/}

          {/*<LinkProtected to="/">*/}
          {/*  <MenuItem onClick={() => {}} className={`${isPathActive(['/']) ? 'bg-muted' : ''}`}>*/}
          {/*    <ShieldCheck />*/}
          {/*    {t('menu.account.confidentiality')}*/}
          {/*  </MenuItem>*/}
          {/*</LinkProtected>*/}

          {/*<LinkProtected to="/">*/}
          {/*  <MenuItem onClick={() => {}} className={`${isPathActive(['/']) ? 'bg-muted' : ''}`}>*/}
          {/*    <HelpCircle />*/}
          {/*    {t('menu.account.help')}*/}
          {/*  </MenuItem>*/}
          {/*</LinkProtected>*/}
        </div>
      </CardContent>
    </Card>
  );
}
